import { useState, useEffect, useRef } from "react";
import Header from "../components/Header";
import { useDesignVersion } from "../DesignVersionContext";
import NoticeV2 from "./NoticeV2";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/Notice.module.css"; // Partner 스타일 재사용
import ErrMsg from "../ErrMsg";
import { ListButton, ConfirmButton } from "../components/Buttons";
import { SelectorInput } from "../components/Inputs";
import { NoticeType, useNotice, useNoticeMutations } from "../NoticeContext";
import { usePartner } from "../PartnerContext";
import Editor from "../components/WYSIWYG";

type EditorAsset = { tempUrl: string; file: File; type: "img" | "video" };

const Notice = () => {
    const { version } = useDesignVersion();
    const { partner } = usePartner();
    const { selectedPartnerId, setSelectedPartnerId } = useNotice();
    const [addNotice, setAddNotice] = useState<boolean>(false);
    const [detailNotice, setDetailNotice] = useState<NoticeType | null>(null);

    if (version === "v2") {
        return <NoticeV2 />;
    }

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>공지사항 관리</h4>
                    <p className={common["text--copy"]}>파트너사별 공지사항을 관리할 수 있습니다.</p>

                    {/* 파트너 선택 */}
                    <div className={style["container--partner-selector"]}>
                        <SelectorInput
                            placeholder="파트너사 선택"
                            value={selectedPartnerId || ""}
                            setValue={setSelectedPartnerId}
                        >
                            {partner.map((p) => (
                                <option key={p.docId} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </SelectorInput>
                    </div>

                    {selectedPartnerId ? (
                        <>
                            <button
                                className={style["button--add-notice"]}
                                onClick={() => setAddNotice(true)}
                                style={{ marginBottom: "24px" }}
                            >
                                공지사항 등록하기
                            </button>
                            <h4 className={common["text--title"]}>공지사항 목록</h4>
                            <p className={common["text--copy"]}>등록된 공지사항을 확인할 수 있습니다.</p>
                            <NoticeTable onOpenDetail={(notice) => setDetailNotice(notice)} />
                        </>
                    ) : (
                        <div className={common["text--alert"]}>
                            <p>파트너사를 선택해주세요.</p>
                        </div>
                    )}
                </div>
            </div>

            {addNotice && selectedPartnerId && (
                <AddNotice
                    close={() => setAddNotice(false)}
                    onSaved={() => {
                        setAddNotice(false);
                    }}
                />
            )}

            {detailNotice && <NoticeDetailPopup notice={detailNotice} close={() => setDetailNotice(null)} />}
        </>
    );
};

const NoticeTable = ({ onOpenDetail }: { onOpenDetail: (notice: NoticeType) => void }) => {
    const { notices } = useNotice();
    const { deleteNoticeWithConfirm } = useNoticeMutations();
    const [loadingDelete, setLoadingDelete] = useState<Record<string, boolean>>({});

    const handleDelete = async (notice: NoticeType) => {
        const noticeId = notice.id;
        setLoadingDelete((prev) => ({ ...prev, [noticeId]: true }));

        try {
            const result = await deleteNoticeWithConfirm(noticeId);
            if (result && result.success) {
                alert("공지사항이 삭제되었습니다.");
            }
        } catch (err: any) {
            console.error(err);
            if (ErrMsg[err?.message]) {
                alert(ErrMsg[err.message]);
            } else {
                alert(err.message || "삭제 중 에러가 발생했습니다");
            }
        } finally {
            setLoadingDelete((prev) => ({ ...prev, [noticeId]: false }));
        }
    };

    if (notices.length === 0) {
        return (
            <div className={common["text--alert"]}>
                <p>등록된 공지사항이 없습니다.</p>
            </div>
        );
    }

    return (
        <>
            <div className={[style["container--rows"], style["container--rows-title"]].join(" ")}>
                <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>제목</p>
                <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>작성일</p>
                <p
                    className={[
                        style["text--eyebrow"],
                        style["text--eyebrow--title"],
                        style["text--eyebrow--number"],
                    ].join(" ")}
                >
                    삭제버튼
                </p>
            </div>
            <div className={style["wrapper--notice-list"]}>
                {notices.map((notice) => (
                    <div
                        key={notice.id}
                        className={style["unit--notice-list"]}
                        onClick={(e) => {
                            if (
                                (e.target instanceof HTMLInputElement && e.target.type === "checkbox") ||
                                e.target instanceof HTMLSpanElement ||
                                e.target instanceof HTMLButtonElement
                            )
                                return;
                            onOpenDetail(notice);
                        }}
                    >
                        <div className={style["container--rows"]}>
                            <p className={style["text--eyebrow"]}>{notice.title}</p>
                            <p className={style["text--eyebrow"]}>
                                {notice.timestamp
                                    ? new Date(Number(notice.timestamp)).toLocaleString()
                                    : "날짜 정보 없음"}
                            </p>
                            <ConfirmButton
                                type="Delete"
                                loading={loadingDelete[notice.id] ?? false}
                                onClick={() => handleDelete(notice)}
                                text="삭제"
                            />
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

const AddNotice = ({ close, onSaved }: { close: () => void; onSaved?: () => void }) => {
    const { createNotice } = useNoticeMutations();
    const [title, setTitle] = useState<string>("");
    const [content, setContent] = useState<string>("");
    const [editorAssets, setEditorAssets] = useState<EditorAsset[]>([]);
    const [active, setActive] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [isMounted, setIsMounted] = useState<boolean>(true);

    useEffect(() => {
        setActive(true);
        return () => setIsMounted(false);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            if (isMounted) close();
        }, 520);
    };

    const saveNotice = async () => {
        if (saving || !isMounted) return;

        setSaving(true);
        try {
            await createNotice(title, content, editorAssets);
            if (isMounted) {
                alert("공지사항이 저장되었습니다.");
                onSaved?.();
            }
        } catch (err: any) {
            console.error(err);
            if (isMounted) {
                alert(err.message || "저장 중 에러가 발생했습니다.");
            }
        } finally {
            if (isMounted) setSaving(false);
        }
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>공지사항 등록하기</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <div>
                        <div style={{ marginBottom: "20px" }}>
                            <h4 className={common["text--overlay"]}>공지 정보 입력</h4>
                            <Editor
                                initialTitle={title}
                                initialContent={content}
                                onTitleChange={setTitle}
                                onChange={setContent}
                                onAssetsChange={setEditorAssets}
                            />
                        </div>
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]} style={{ display: "flex", gap: "10px" }}>
                    <ListButton type="Default" text="취소" onClick={hide} />
                    <ListButton text="등록하기" onClick={saveNotice} loading={saving} />
                </div>
            </div>
        </div>
    );
};

const NoticeDetailPopup = ({ notice, close }: { notice: NoticeType; close: () => void }) => {
    const { deleteNoticeWithConfirm, sanitizeHtml } = useNoticeMutations();
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setActive(true);
    }, []);

    // 렌더 후 비디오 재생 가능 속성 강제 부여 + 클릭 가능 보장 (안전망)
    useEffect(() => {
        if (!active) return;
        const root = contentRef.current;
        if (!root) return;

        const videos = Array.from(root.querySelectorAll("video")) as HTMLVideoElement[];
        if (videos.length > 0) {
            // 저장 시점에서 이미 설정되어 있어야 하므로, 누락된 속성만 보완 (안전망)
            videos.forEach((v) => {
                if (!v.hasAttribute("controls")) {
                    console.warn("Video missing controls attribute, adding as fallback");
                    v.setAttribute("controls", "true");
                }
                if (!v.hasAttribute("playsinline")) {
                    console.warn("Video missing playsinline attribute, adding as fallback");
                    v.setAttribute("playsinline", "true");
                }
                if (!v.hasAttribute("webkit-playsinline")) {
                    console.warn("Video missing webkit-playsinline attribute, adding as fallback");
                    v.setAttribute("webkit-playsinline", "true");
                }
                if (v.style.pointerEvents === "none") {
                    console.warn("Video has pointer-events: none, fixing as fallback");
                    v.style.pointerEvents = "auto";
                }
            });
        }
    }, [active, notice.content]);

    const handleClose = () => {
        setActive(false);
        setTimeout(() => close(), 520);
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            const result = await deleteNoticeWithConfirm(notice.id);
            if (result && result.success) {
                alert("공지사항이 삭제되었습니다.");
                handleClose();
            }
        } catch (err: any) {
            console.error(err);
            alert(err.message || "삭제 중 에러가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>공지사항 상세보기</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <div>
                        <div className={style["wrapper--notice--title"]}>
                            <h4 className={style["text--notice"]}>
                                <span>제목</span>
                                {notice.title}
                            </h4>
                            <h4 className={style["text--notice"]}>
                                <span>작성일</span>
                                {notice.timestamp ? new Date(Number(notice.timestamp)).toLocaleString() : "-"}
                            </h4>
                        </div>
                        <div
                            ref={contentRef}
                            className={style["container--notice"]}
                            style={{ pointerEvents: "auto" }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(notice.content) }}
                        />
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ConfirmButton type="Delete" loading={loading} onClick={handleDelete} text="삭제" />
                    <ListButton type="Default" text="닫기" onClick={handleClose} />
                </div>
            </div>
        </div>
    );
};

export default Notice;
